import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useSelector } from 'react-redux';
import { uid } from 'react-uid';
import {
  CommissionAttemptResult as CommissionResult,
  Job,
  JobProgress,
  JobStatusUpdate,
  JobStepStatus,
  PhaseWiring,
  ResponseGetJob,
  useUpdateJob,
} from '../../../../app/ApiGen';
import { CommissionAttemptResult, JobStatus } from '../../../../app/enums';
import { JobStatusValue } from '../../../../components/CustomFields/JobStatus';
import { errorSnackbar } from '../../../../components/ErrorSnackbar';
import { TextField } from '../../../../components/TextField';
import { Card, FormActions, List, ListHeader, ListItem, Section } from '../../../../design-system';
import { CardTitle } from '../../../../design-system/Card/styled';
import { fullStateSelector } from '../../../../store/root';
import { DateFormatDateFns, TimeFormatDateFns } from '../../../../utils/dateFormat';
import {
  mapJobProgressUpdateToRequestUpdateJob,
  mapJobStatusUpdateToRequestUpdateJob,
} from '../../../../utils/jobs/mappers';
import { hasCommissioningSteps } from '../../../../utils/jobs/stages';
import { canUpdateJobStatus } from '../../../../utils/jobs/status';
import type { Normalised } from '../../../../utils/request';
import { InstallationStagesSection } from './InstallationStagesSection';

interface JobStatusSectionProps {
  job: Normalised<Job>;
  refetchJob: () => Promise<ResponseGetJob | null>;
}

interface InstallersDetails {
  created: string;
  email: string;
  id: string;
  name: string;
}

type JobProgressSectionProps = JobStatusSectionProps & { jobProgress: JobProgress };

const JobProgressSection: React.VFC<JobProgressSectionProps> = ({ job, jobProgress, refetchJob }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { mutate } = useUpdateJob({ jobId: job.id });

  const assignedInstallersDetailed = job.assignedInstallers as unknown as InstallersDetails[];

  const [stepStatuses, setStepStatuses] = React.useState<JobProgress>(jobProgress);

  const [manualCommissionDetails, setManualCommissionDetails] = React.useState({
    chargePointCount: 1,
    powerSensorCount: 1,
    installedChargePointId: '',
    supplyCurrentLimit: 32,
    l1WiredToPhase: 0,
    powerSensorCTOnThreePhases: false,
    installerId: '',
  });

  const handleUpdateStepStatus = (step: { name: keyof JobProgress; status: JobStepStatus }) => {
    const updateJobRequest = mapJobProgressUpdateToRequestUpdateJob({
      job: { id: job.id, progress: jobProgress },
      update: {
        commissioningDetails: {
          ...manualCommissionDetails,
          l1WiredToPhase: manualCommissionDetails.l1WiredToPhase as PhaseWiring,
        },
        step,
      },
    });
    mutate(updateJobRequest)
      .then(async () => {
        await refetchJob();
        enqueueSnackbar(`Status for ${step.name} updated to "${step.status}"`, { variant: 'success' });
      })
      .catch((err) => {
        errorSnackbar(`Unable to update status for ${step.name}`, closeSnackbar, enqueueSnackbar, err);
      });
  };

  return (
    <Section>
      <CardTitle>Job Progress</CardTitle>

      <List inset variant="bordered">
        <ListHeader>
          <Grid container>
            <Grid item xs={4}>
              Step
            </Grid>
            <Grid item xs={4}>
              Result
            </Grid>
            <Grid item xs={4}>
              Action
            </Grid>
          </Grid>
        </ListHeader>
        <ListItem>
          <Grid container>
            <Grid item xs={4}>
              Pre-start HSE (JSEA)
            </Grid>
            <Grid item xs={4}>
              {jobProgress.preWorkForm}
            </Grid>
            {job.status !== JobStatus.Complete && (
              <Grid item xs={4}>
                <Select
                  value={stepStatuses.preWorkForm}
                  onChange={(e) => setStepStatuses({ ...stepStatuses, preWorkForm: e.target.value as JobStepStatus })}
                  displayEmpty
                  variant="outlined"
                  style={{ minWidth: 150 }}
                >
                  <MenuItem value="" disabled>
                    Select Status
                  </MenuItem>
                  <MenuItem value="ready">Ready</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="manually completed">Manually Completed</MenuItem>
                </Select>
                <Button
                  color="primary"
                  onClick={() => handleUpdateStepStatus({ name: 'preWorkForm', status: stepStatuses.preWorkForm })}
                  variant="contained"
                  disabled={!stepStatuses.preWorkForm}
                >
                  Update
                </Button>
              </Grid>
            )}
          </Grid>
        </ListItem>

        <ListItem>
          <Grid container>
            <Grid item xs={4}>
              Commission charger
            </Grid>
            <Grid item xs={4}>
              {jobProgress.commissioning}
            </Grid>
            {job.status !== JobStatus.Complete && (
              <Grid item xs={4}>
                <Select
                  value={stepStatuses.commissioning}
                  onChange={(e) => setStepStatuses({ ...stepStatuses, commissioning: e.target.value as JobStepStatus })}
                  displayEmpty
                  variant="outlined"
                  style={{ minWidth: 150 }}
                >
                  <MenuItem value="" disabled>
                    Select Status
                  </MenuItem>
                  <MenuItem value="ready">Ready</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="manually completed">Manually Completed</MenuItem>
                </Select>
                <Button
                  color="primary"
                  onClick={() => handleUpdateStepStatus({ name: 'commissioning', status: stepStatuses.commissioning })}
                  variant="contained"
                  disabled={!stepStatuses.commissioning}
                >
                  Update
                </Button>
              </Grid>
            )}
          </Grid>
        </ListItem>
        {stepStatuses.commissioning === 'manually completed' && job.status !== JobStatus.Complete && (
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Charge Point Count"
                  type="number"
                  value={manualCommissionDetails.chargePointCount}
                  onChange={(e) =>
                    setManualCommissionDetails({ ...manualCommissionDetails, chargePointCount: +e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Power Sensor Count"
                  type="number"
                  value={manualCommissionDetails.powerSensorCount}
                  onChange={(e) =>
                    setManualCommissionDetails({ ...manualCommissionDetails, powerSensorCount: +e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Installed Charge Point ID"
                  value={manualCommissionDetails.installedChargePointId}
                  onChange={(e) =>
                    setManualCommissionDetails({ ...manualCommissionDetails, installedChargePointId: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Supply Current Limit"
                  type="number"
                  value={manualCommissionDetails.supplyCurrentLimit}
                  onChange={(e) =>
                    setManualCommissionDetails({ ...manualCommissionDetails, supplyCurrentLimit: +e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="L1 Wired to Phase"
                  select
                  value={manualCommissionDetails.l1WiredToPhase.toString()}
                  onChange={(e) =>
                    setManualCommissionDetails({
                      ...manualCommissionDetails,
                      l1WiredToPhase: parseInt(e.target.value, 10),
                    })
                  }
                  fullWidth
                  variant="outlined"
                >
                  <MenuItem value="0">L1</MenuItem>
                  <MenuItem value="1">L2</MenuItem>
                  <MenuItem value="2">L3</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Power Sensor CT on Three Phases"
                  select
                  value={manualCommissionDetails.powerSensorCTOnThreePhases ? 'yes' : 'no'}
                  onChange={(e) =>
                    setManualCommissionDetails({
                      ...manualCommissionDetails,
                      powerSensorCTOnThreePhases: e.target.value === 'yes',
                    })
                  }
                  fullWidth
                  variant="outlined"
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Installer"
                  select
                  value={manualCommissionDetails.installerId}
                  onChange={(e) =>
                    setManualCommissionDetails({
                      ...manualCommissionDetails,
                      installerId: e.target.value,
                    })
                  }
                  fullWidth
                  variant="outlined"
                >
                  {assignedInstallersDetailed.map(({ id, email, name }) => (
                    <MenuItem key={id} value={id}>
                      {name} - ({email})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </ListItem>
        )}
        {job.details.isAGLJob && (
          <ListItem>
            <Grid container>
              <Grid item xs={4}>
                Completion / Sign-off
              </Grid>
              <Grid item xs={4}>
                {jobProgress.postWorkForm}
              </Grid>
              {job.status !== JobStatus.Complete && (
                <Grid item xs={4}>
                  <Select
                    value={stepStatuses.postWorkForm}
                    onChange={(e) =>
                      setStepStatuses({ ...stepStatuses, postWorkForm: e.target.value as JobStepStatus })
                    }
                    displayEmpty
                    variant="outlined"
                    style={{ minWidth: 150 }}
                  >
                    <MenuItem value="" disabled>
                      Select Status
                    </MenuItem>
                    <MenuItem value="ready">Ready</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="manually completed">Manually Completed</MenuItem>
                  </Select>
                  <Button
                    color="primary"
                    onClick={() => handleUpdateStepStatus({ name: 'postWorkForm', status: stepStatuses.postWorkForm })}
                    variant="contained"
                    disabled={!stepStatuses.postWorkForm}
                  >
                    Update
                  </Button>
                </Grid>
              )}
            </Grid>
          </ListItem>
        )}
        <ListItem>
          <Grid container>
            <Grid item xs={4}>
              Certificate of Compliance
            </Grid>
            <Grid item xs={4}>
              {jobProgress.certificateOfCompliance}
            </Grid>
          </Grid>
        </ListItem>
      </List>
      <Box m={1} />
    </Section>
  );
};

const HumanReadableCommissionResult: Record<CommissionResult, string> = {
  [CommissionAttemptResult.Success]: 'Successfully commissioned',
  [CommissionAttemptResult.AccountNotActive]: 'Failed - Customer account not active',
  [CommissionAttemptResult.ChargePointAlreadyClaimed]: 'Failed - Charge point already claimed',
  [CommissionAttemptResult.ChargePointUnresponsive]: 'Failed - Charge point unresponsive',
  [CommissionAttemptResult.FailedForUnknownReason]: 'Failed for unknown reason',
  [CommissionAttemptResult.JobHasIncorrectParameters]: 'Failed - Job has incorrect parameters',
};

export const JobStatusSection: React.VFC<JobStatusSectionProps> = ({ job, refetchJob }: JobStatusSectionProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { mutate } = useUpdateJob({ jobId: job.id });

  const { isEvnexAdmin } = useSelector(fullStateSelector);
  const jobStatusCanBeUpdated = canUpdateJobStatus({ isEvnexAdmin, job });

  const handleUpdateJobStatus = (status: JobStatusUpdate) => {
    mutate(
      mapJobStatusUpdateToRequestUpdateJob({
        job: { id: job.id, details: job.details },
        update: { status },
      }),
    )
      .then(async () => {
        await refetchJob();
        enqueueSnackbar('Job status updated', { variant: 'success' });
      })
      .catch((err) => {
        errorSnackbar('Unable to edit job status', closeSnackbar, enqueueSnackbar, err);
      });
  };

  return (
    <Section>
      <Card title="Job Status">
        <List>
          <ListItem>
            <Grid container spacing={3}>
              <Grid item>
                <JobStatusValue value={job.status} />
              </Grid>
              {!jobStatusCanBeUpdated && <Grid item>The job status cannot be updated.</Grid>}
            </Grid>
          </ListItem>
        </List>
        {jobStatusCanBeUpdated && (
          <FormActions>
            <Button color="inherit" onClick={() => handleUpdateJobStatus(JobStatus.Cancelled)} variant="outlined">
              Cancel Job
            </Button>
            <Box m={2} />
            <Button
              color="warning"
              onClick={() => handleUpdateJobStatus(JobStatus.ManuallyCommissioned)}
              variant="contained"
            >
              Move Job To Manually Commissioned
            </Button>
            <Box m={2} />
            {job.status !== JobStatus.InProgress && (
              <Button color="info" onClick={() => handleUpdateJobStatus(JobStatus.InProgress)} variant="contained">
                Move Job To In Progress
              </Button>
            )}
          </FormActions>
        )}
        <Box m={1} />
        <List inset variant="bordered">
          <ListHeader>
            <Grid container>
              <Grid item xs={4}>
                Date
              </Grid>
              <Grid item xs={8}>
                Result
              </Grid>
            </Grid>
          </ListHeader>
          {job.commissionResults && job.commissionResults.length > 0 ? (
            job.commissionResults.map(({ date, result }) => (
              <ListItem key={uid(date)}>
                <Grid container>
                  <Grid item xs={4}>
                    {format(new Date(date), `${DateFormatDateFns.Long} ${TimeFormatDateFns.WithSeconds}`)}
                  </Grid>
                  <Grid item xs={8}>
                    {HumanReadableCommissionResult[result]}
                  </Grid>
                </Grid>
              </ListItem>
            ))
          ) : (
            <ListItem>
              <Grid container justifyContent="center">
                <Grid item>No commissioning attempts</Grid>
              </Grid>
            </ListItem>
          )}
        </List>
        <Box m={1} />
        {job.details.progress !== undefined &&
          !(job.details.installationStages && hasCommissioningSteps(job.details.installationStages)) && (
            <JobProgressSection job={job} jobProgress={job.details.progress} refetchJob={refetchJob} />
          )}
        {job.details.installationStages !== undefined && (
          <InstallationStagesSection
            canUpdate={job.status !== JobStatus.Complete && hasCommissioningSteps(job.details.installationStages)}
            installationStages={job.details.installationStages}
            jobId={job.id}
            refetchJob={refetchJob}
          />
        )}
      </Card>
    </Section>
  );
};
